import React, { useState, useEffect } from 'react';
import {
  Shield,
  Download,
  Upload,
  Calendar,
  Clock,
  Mail,
  Lock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Database,
  FileArchive,
  Eye,
  Trash2,
  Settings,
  Play,
  Save,
  Info,
  Image as ImageIcon,
  Mic,
  FileText,
  HardDrive,
  Archive,
  History
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  BackupSettings,
  BackupHistoryRecord,
  BackupStatus,
  BackupFrequency,
  ExportOptions,
  RestoreOptions,
  RestoreMode,
} from '../types/backup';
import { backupService } from '../utils/backup/backupService';
import { importService } from '../utils/backup/importService';

interface DataStorageBackupProps {
  user: any;
  onClose?: () => void;
}

export default function DataStorageBackup({ user, onClose }: DataStorageBackupProps) {
  // Settings State
  const [settings, setSettings] = useState<BackupSettings>({
    enabled: false,
    frequency: 'monthly',
    dayOfMonth: 1,
    timeOfDay: '02:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    emailTo: [user.email],
    emailOnSuccess: true,
    emailOnFailure: true,
    emailWeeklySummary: false,
    encryption: {
      enabled: true,
      algorithm: 'AES-256-GCM',
      masterKeyId: '',
      createdAt: new Date().toISOString(),
    },
    includePhotos: true,
    includeVoiceMemos: true,
    includeNotes: true,
    includeWells: true,
    includeProduction: true,
    includeTickets: true,
    includeInventory: true,
    includeFinancial: true,
    includeUsers: true,
    includeAnalytics: true,
    retentionDays: 90,
    autoCleanup: true,
    reminderDays: [80, 87],
    totalBackupsCreated: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: user.id,
    updatedBy: user.id,
  });

  const [backupHistory, setBackupHistory] = useState<BackupHistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [testingBackup, setTestingBackup] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [storageUsed, setStorageUsed] = useState({ used: 0, total: 0 });

  // Restore State
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoreMode, setRestoreMode] = useState<RestoreMode>('merge');
  const [restoreValidated, setRestoreValidated] = useState(false);

  useEffect(() => {
    loadBackupSettings();
    loadBackupHistory();
    calculateStorageUsage();
  }, [user.companyId]);

  const loadBackupSettings = async () => {
    try {
      const savedSettings = await backupService.getBackupSettings(user.companyId);
      if (savedSettings) {
        setSettings(savedSettings);
      }
    } catch (error) {
      console.error('Error loading backup settings:', error);
    }
  };

  const loadBackupHistory = async () => {
    try {
      const history = await backupService.getBackupHistory(user.companyId);
      setBackupHistory(history);
    } catch (error) {
      console.error('Error loading backup history:', error);
    }
  };

  const calculateStorageUsage = async () => {
    try {
      const usage = await backupService.calculateStorageUsage(user.companyId);
      setStorageUsed(usage);
    } catch (error) {
      console.error('Error calculating storage:', error);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await backupService.saveBackupSettings(user.companyId, {
        ...settings,
        updatedAt: new Date().toISOString(),
        updatedBy: user.id,
      });
      toast.success('Backup settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save backup settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestBackup = async () => {
    setTestingBackup(true);
    try {
      toast.info('Generating test backup...');
      const result = await backupService.createManualBackup(user.companyId, user, {
        ...settings,
        includePhotos: settings.includePhotos,
        includeVoiceMemos: settings.includeVoiceMemos,
        includeNotes: settings.includeNotes,
      });
      
      toast.success(`Test backup created successfully! Size: ${formatFileSize(result.size)}`);
      await loadBackupHistory();
      await calculateStorageUsage();
    } catch (error) {
      console.error('Error creating test backup:', error);
      toast.error('Failed to create test backup');
    } finally {
      setTestingBackup(false);
    }
  };

  const handleManualExport = async () => {
    setExporting(true);
    try {
      toast.info('Preparing export...');
      
      const exportOptions: ExportOptions = {
        format: 'json-zip',
        includePhotos: settings.includePhotos,
        includeVoiceMemos: settings.includeVoiceMemos,
        includeNotes: settings.includeNotes,
        includeWells: settings.includeWells,
        includeProduction: settings.includeProduction,
        includeTickets: settings.includeTickets,
        includeInventory: settings.includeInventory,
        includeFinancial: settings.includeFinancial,
        includeUsers: settings.includeUsers,
        includeAnalytics: settings.includeAnalytics,
        encrypt: settings.encryption.enabled,
        downloadMethod: 'direct',
      };

      const blob = await backupService.exportData(user.companyId, user, exportOptions);
      
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${user.company || 'company'}-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Export complete! Download started.');
      await loadBackupHistory();
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      toast.error('Please upload a .zip backup file');
      return;
    }

    setRestoreFile(file);
    setRestoreValidated(false);
    toast.info('Backup file selected. Click "Validate Backup" to verify.');
  };

  const handleValidateBackup = async () => {
    if (!restoreFile) return;

    setLoading(true);
    try {
      const validation = await importService.validateBackup(restoreFile, user.companyId);
      
      if (validation.valid) {
        setRestoreValidated(true);
        toast.success(`Backup is valid! Contains ${validation.recordCounts.wells} wells, ${validation.recordCounts.productionEntries} production entries.`);
      } else {
        toast.error(`Backup validation failed: ${validation.errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Error validating backup:', error);
      toast.error('Failed to validate backup file');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRestore = async () => {
    if (!restoreFile || !restoreValidated) {
      toast.error('Please validate the backup file first');
      return;
    }

    const confirmMessage = restoreMode === 'full' 
      ? 'WARNING: This will DELETE ALL CURRENT DATA and replace with backup data. This cannot be undone. Are you sure?'
      : 'This will restore data from the backup file. Existing data will be preserved unless conflicts occur. Continue?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setRestoring(true);
    try {
      toast.info('Starting restore process...');

      const restoreOptions: RestoreOptions = {
        mode: restoreMode,
        conflictResolution: {
          wells: 'merge',
          production: 'merge',
          users: 'skip',
        },
        createEmergencyBackup: true,
        validateBeforeRestore: true,
        onProgress: (progress) => {
          // Update progress UI (could add progress bar here)
        },
      };

      const result = await importService.restoreFromBackup(
        restoreFile,
        user.companyId,
        user,
        restoreOptions
      );

      if (result.success) {
        toast.success(`Restore complete! Restored ${result.itemsRestored.wells} wells, ${result.itemsRestored.productionEntries} production entries.`);
        
        if (result.warnings.length > 0) {
          toast.warning(`${result.warnings.length} warnings occurred during restore. Check logs for details.`);
        }

        // Refresh page to load new data
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        toast.error(`Restore failed: ${result.errors.map((e: any) => e.message).join(', ')}`);
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error('Failed to restore backup');
    } finally {
      setRestoring(false);
    }
  };

  const handleDownloadBackup = async (backup: BackupHistoryRecord) => {
    try {
      toast.info('Preparing download...');
      const downloadUrl = await backupService.getDownloadUrl(backup.id, user.companyId);
      window.open(downloadUrl, '_blank');
      toast.success('Download started!');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Failed to download backup');
    }
  };

  const handleDeleteBackup = async (backupId: string) => {
    if (!window.confirm('Are you sure you want to delete this backup? This cannot be undone.')) {
      return;
    }

    try {
      await backupService.deleteBackup(backupId, user.companyId);
      toast.success('Backup deleted successfully');
      await loadBackupHistory();
      await calculateStorageUsage();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const getStatusBadge = (status: BackupStatus) => {
    const variants = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      partial: 'bg-yellow-100 text-yellow-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      pending: 'bg-gray-100 text-gray-800',
    };

    const icons = {
      success: CheckCircle,
      failed: XCircle,
      partial: AlertTriangle,
      'in-progress': RefreshCw,
      pending: Clock,
    };

    const Icon = icons[status];

    return (
      <Badge className={variants[status]}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getNextBackupDate = (): string => {
    if (!settings.enabled) return 'Not scheduled';
    
    const now = new Date();
    let nextDate: Date;

    if (settings.frequency === 'monthly') {
      nextDate = new Date(now.getFullYear(), now.getMonth() + 1, settings.dayOfMonth || 1);
      if (nextDate < now) {
        nextDate = new Date(now.getFullYear(), now.getMonth() + 2, settings.dayOfMonth || 1);
      }
    } else if (settings.frequency === 'weekly') {
      nextDate = new Date(now);
      const daysUntilNext = ((settings.dayOfWeek || 0) - now.getDay() + 7) % 7;
      nextDate.setDate(now.getDate() + (daysUntilNext === 0 ? 7 : daysUntilNext));
    } else {
      // Custom
      const customDays = settings.customDays || [1, 15];
      const currentDay = now.getDate();
      const nextDay = customDays.find(d => d > currentDay) || customDays[0];
      nextDate = new Date(now.getFullYear(), nextDay > currentDay ? now.getMonth() : now.getMonth() + 1, nextDay);
    }

    return nextDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }) + ` at ${settings.timeOfDay}`;
  };

  // Only admins/managers can access this page
  if (user.role !== 'admin' && user.role !== 'manager' && user.role !== 'superadmin') {
    return (
      <div className="p-6 text-center">
        <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
        <p className="text-gray-600">Only administrators and managers can access backup & recovery features.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="h-7 w-7 mr-2 text-blue-600" />
            Backup & Recovery
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Protect your critical oil & gas data with automated backups and disaster recovery
          </p>
        </div>
        {onClose && (
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        )}
      </div>

      {/* Storage Overview Cards */}
      <Card className="border-none shadow-none bg-transparent">
        <CardContent className="p-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Backup Storage Used</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatFileSize(storageUsed.used)}
                  </p>
                  <p className="text-xs text-blue-500 mt-1">
                    of {formatFileSize(storageUsed.total)} quota
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Database className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-4 border border-green-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Total Backups</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {backupHistory.length}
                  </p>
                  <p className="text-xs text-green-500 mt-1">
                    {settings.retentionDays} day retention
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileArchive className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Compression Savings</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {storageUsed.total > 0 
                      ? ((1 - storageUsed.used / storageUsed.total) * 100).toFixed(0) + '%'
                      : '75%'}
                  </p>
                  <p className="text-xs text-purple-500 mt-1">
                    GZIP compression active
                  </p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Archive className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Automated Backups Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-blue-600" />
            Automated Backups
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between pb-4 border-b">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${settings.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                {settings.enabled ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  Status: {settings.enabled ? 'Active' : 'Disabled'}
                </p>
                {settings.enabled && (
                  <p className="text-sm text-gray-600">
                    Next backup: {getNextBackupDate()}
                  </p>
                )}
                {settings.lastBackupDate && (
                  <p className="text-xs text-gray-500">
                    Last backup: {new Date(settings.lastBackupDate).toLocaleString()}
                    {settings.lastBackupSize && ` (${formatFileSize(settings.lastBackupSize)})`}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
              className={`relative w-16 h-8 rounded-full transition-colors ${
                settings.enabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
                  settings.enabled ? 'translate-x-9' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Schedule Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Backup Frequency
              </label>
              <select
                value={settings.frequency}
                onChange={(e) => setSettings({ ...settings, frequency: e.target.value as BackupFrequency })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom Days</option>
              </select>
            </div>

            {settings.frequency === 'weekly' && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Day of Week
                </label>
                <select
                  value={settings.dayOfWeek || 0}
                  onChange={(e) => setSettings({ ...settings, dayOfWeek: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                </select>
              </div>
            )}

            {settings.frequency === 'monthly' && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Day of Month
                </label>
                <select
                  value={settings.dayOfMonth || 1}
                  onChange={(e) => setSettings({ ...settings, dayOfMonth: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>
                      {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of month
                    </option>
                  ))}
                </select>
              </div>
            )}

            {settings.frequency === 'custom' && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Custom Days (comma-separated)
                </label>
                <Input
                  type="text"
                  placeholder="1, 15"
                  value={settings.customDays?.join(', ') || ''}
                  onChange={(e) => {
                    const days = e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d >= 1 && d <= 31);
                    setSettings({ ...settings, customDays: days });
                  }}
                  className="h-10"
                />
                <p className="text-xs text-gray-500 mt-1">Enter days of month (1-31), minimum 7 days apart</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Backup Time
              </label>
              <Input
                type="time"
                value={settings.timeOfDay}
                onChange={(e) => setSettings({ ...settings, timeOfDay: e.target.value })}
                className="h-10"
              />
            </div>
          </div>

          {/* Email Configuration */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Mail className="h-4 w-4 mr-2 text-gray-500" />
              Email Notifications
            </label>
            <Input
              type="email"
              placeholder="admin@company.com"
              value={settings.emailTo[0] || ''}
              onChange={(e) => setSettings({ ...settings, emailTo: [e.target.value] })}
              className="h-10 mb-2"
            />
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={settings.emailOnSuccess}
                  onChange={(e) => setSettings({ ...settings, emailOnSuccess: e.target.checked })}
                  className="rounded"
                />
                <span>Email on successful backup</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={settings.emailOnFailure}
                  onChange={(e) => setSettings({ ...settings, emailOnFailure: e.target.checked })}
                  className="rounded"
                />
                <span>Email on backup failure</span>
              </label>
            </div>
          </div>

          {/* Include Options - User Checkboxes */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-3 block">
              Include in Backups
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={settings.includePhotos}
                  onChange={(e) => setSettings({ ...settings, includePhotos: e.target.checked })}
                  className="rounded"
                />
                <ImageIcon className="h-4 w-4 text-blue-600" />
                <span>Photos</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={settings.includeVoiceMemos}
                  onChange={(e) => setSettings({ ...settings, includeVoiceMemos: e.target.checked })}
                  className="rounded"
                />
                <Mic className="h-4 w-4 text-purple-600" />
                <span>Voice Memos</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={settings.includeNotes}
                  onChange={(e) => setSettings({ ...settings, includeNotes: e.target.checked })}
                  className="rounded"
                />
                <FileText className="h-4 w-4 text-green-600" />
                <span>Notes</span>
              </label>
            </div>
          </div>

          {/* Encryption Status */}
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center space-x-3">
              <Lock className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  🔒 Encryption: {settings.encryption.enabled ? 'Enabled' : 'Disabled'}
                </p>
                <p className="text-xs text-gray-600">
                  {settings.encryption.algorithm} - Your backups are encrypted for maximum security
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t">
            <Button
              onClick={handleSaveSettings}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button
              onClick={handleTestBackup}
              disabled={testingBackup || !settings.enabled}
              variant="outline"
              className="flex-1"
            >
              <Play className="h-4 w-4 mr-2" />
              {testingBackup ? 'Creating...' : 'Test Backup Now'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Manual Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Download className="h-5 w-5 mr-2 text-green-600" />
            Manual Export
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Need an immediate backup? Export all your data instantly.
          </p>
          <Button
            onClick={handleManualExport}
            disabled={exporting}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export All Data Now'}
          </Button>
        </CardContent>
      </Card>

      {/* Import & Restore */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Upload className="h-5 w-5 mr-2 text-orange-600" />
            Import & Restore
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Restore data from a backup file. Choose your restore mode carefully.
          </p>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Choose Backup File
            </label>
            <Input
              type="file"
              accept=".zip"
              onChange={handleFileUpload}
              className="h-10"
            />
            {restoreFile && (
              <p className="text-xs text-gray-600 mt-1">
                Selected: {restoreFile.name} ({formatFileSize(restoreFile.size)})
              </p>
            )}
          </div>

          {restoreFile && (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Restore Mode
                </label>
                <div className="space-y-2">
                  <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="restoreMode"
                      value="merge"
                      checked={restoreMode === 'merge'}
                      onChange={(e) => setRestoreMode(e.target.value as RestoreMode)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-sm">Merge (Safe)</p>
                      <p className="text-xs text-gray-600">Keep existing data, add new items from backup</p>
                    </div>
                  </label>
                  <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="restoreMode"
                      value="selective"
                      checked={restoreMode === 'selective'}
                      onChange={(e) => setRestoreMode(e.target.value as RestoreMode)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-sm">Selective</p>
                      <p className="text-xs text-gray-600">Choose specific items to restore</p>
                    </div>
                  </label>
                  <label className="flex items-start space-x-3 p-3 border-2 border-red-200 rounded-lg cursor-pointer hover:bg-red-50">
                    <input
                      type="radio"
                      name="restoreMode"
                      value="full"
                      checked={restoreMode === 'full'}
                      onChange={(e) => setRestoreMode(e.target.value as RestoreMode)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-sm text-red-700">Full Restore (Dangerous)</p>
                      <p className="text-xs text-red-600">⚠️ Deletes ALL current data and replaces with backup</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={handleValidateBackup}
                  disabled={loading}
                  variant="outline"
                  className="flex-1"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {loading ? 'Validating...' : 'Validate Backup'}
                </Button>
                <Button
                  onClick={handleStartRestore}
                  disabled={!restoreValidated || restoring}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {restoring ? 'Restoring...' : 'Start Restore'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Backup History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <History className="h-5 w-5 mr-2 text-purple-600" />
            Backup History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {backupHistory.length === 0 ? (
            <div className="text-center py-8">
              <FileArchive className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">No backups yet</p>
              <p className="text-sm text-gray-500">Create your first backup to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Downloads</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {backupHistory.map((backup) => (
                    <tr key={backup.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(backup.startedAt).toLocaleDateString()}
                        <br />
                        <span className="text-xs text-gray-500">
                          {new Date(backup.startedAt).toLocaleTimeString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant="outline">
                          {backup.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatFileSize(backup.manifest.totalSize)}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(backup.status)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {backup.downloadCount}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <Button
                          onClick={() => handleDownloadBackup(backup)}
                          size="sm"
                          variant="outline"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteBackup(backup.id)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disaster Recovery Info */}
      <Card className="border-orange-200 bg-orange-50">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <Shield className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Disaster Recovery</h4>
              <p className="text-sm text-gray-700">
                In case of data loss or system failure, contact support to initiate a restore from backup. 
                Restores are performed by administrators only to prevent accidental data overwrites.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}